/**
 * Trading Domain Constants
 *
 * Single source of truth for all trading domain enum values.
 * Import from here — never hardcode these strings elsewhere.
 */

// Trading types
export const TRADING_TYPE = { INTRADAY: "INTRADAY", POSITIONAL: "POSITIONAL" } as const;
export const INTRADAY_EXIT_MODE = { SAME_DAY: "SAME_DAY", NEXT_DAY_BTST: "NEXT_DAY_BTST" } as const;
export const OPTION_TYPE = { CE: "CE", PE: "PE" } as const;
export const ACTION_TYPE = { BUY: "BUY", SELL: "SELL" } as const;
export const PRODUCT_TYPE = { MIS: "MIS", NRML: "NRML" } as const;
export const EXPIRY_TYPE_CONST = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  NEXT_WEEKLY: "next_weekly",
  NEXT_MONTHLY: "next_monthly",
} as const;

// Option arrays for custom SegmentedControl / Dropdown components
export const TRADING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: TRADING_TYPE.INTRADAY, label: "INTRADAY" },
  { value: TRADING_TYPE.POSITIONAL, label: "POSITIONAL" },
];
export const PRODUCT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: PRODUCT_TYPE.MIS, label: "MIS" },
  { value: PRODUCT_TYPE.NRML, label: "NRML" },
];
// Order-placement exchanges. NFO/BFO are the F&O segments (options); NSE/BSE
// the cash segments. Segment drives the broker algo_id branch (NSE-family vs
// BSE-family) for Zebu marketplace orders.
export const EXCHANGE = { NFO: "NFO", BFO: "BFO", NSE: "NSE", BSE: "BSE" } as const;
export const EXCHANGE_OPTIONS: { value: string; label: string }[] = [
  { value: EXCHANGE.NFO, label: "NFO (NSE F&O)" },
  { value: EXCHANGE.BFO, label: "BFO (BSE F&O)" },
  { value: EXCHANGE.NSE, label: "NSE (cash)" },
  { value: EXCHANGE.BSE, label: "BSE (cash)" },
];
export const INTRADAY_EXIT_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: INTRADAY_EXIT_MODE.SAME_DAY, label: "Same Day" },
  { value: INTRADAY_EXIT_MODE.NEXT_DAY_BTST, label: "Next Day (STBT/BTST)" },
];

// Selection
export const SELECTION_METHOD = {
  ATM_POINTS: "ATM_POINTS",
  ATM_PERCENT: "ATM_PERCENT",
  PREMIUM: "PREMIUM",
  PERCENTAGE_OF_STRADDLE_PREMIUM: "PERCENTAGE_OF_STRADDLE_PREMIUM",
  STRADDLE_WIDTH: "STRADDLE_WIDTH",
  PERCENTAGE_OF_UNDERLYING: "PERCENTAGE_OF_UNDERLYING",
} as const;
export const PREMIUM_OPERATOR = { CLOSEST: "CLOSEST", GTE: "GTE", LTE: "LTE" } as const;

// Risk management
//
// Phase 6 — UL (underlying-based) types mirrored from
// `libs/shared/shared/constants.py` (RiskManagementType.UL_POINTS/UL_PERCENTAGE,
// TargetProfitType.UL_POINTS/UL_PERCENTAGE, TslType.UL_POINTS/UL_PERCENTAGE,
// WatType.UL_POINTS_UP/UL_POINTS_DOWN/UL_PCT_UP/UL_PCT_DOWN). Any change here
// MUST be paired with the backend change and vice versa. Gated at runtime by
// `UL_MONITOR_ENABLED` — the H3 validators in `strategy_manager.py` reject
// these types when the flag is off.
export const RISK_MANAGEMENT_TYPE = {
  POINTS: "POINTS",
  PERCENTAGE: "PERCENTAGE",
  RANGE: "RANGE",
  UL_POINTS: "UL_POINTS",
  UL_PERCENTAGE: "UL_PERCENTAGE",
} as const;
export const MTM_TYPE = { TOTAL_MTM: "TOTAL_MTM", COMBINED_PREMIUM_PERCENT: "COMBINED_PREMIUM_PERCENT" } as const;
export const RE_ENTRY_TYPE = { SL_REENTRY: "SL_REENTRY", SL_RECOST: "SL_RECOST", SL_REEXEC: "SL_REEXEC" } as const;
export const RE_EXECUTE_TYPE = { TP_REEXEC: "TP_REEXEC" } as const;
export const TARGET_PROFIT_TYPE = {
  POINTS: "POINTS",
  PERCENTAGE: "PERCENTAGE",
  UL_POINTS: "UL_POINTS",
  UL_PERCENTAGE: "UL_PERCENTAGE",
} as const;
export const TSL_TYPE = {
  POINTS: "POINTS",
  PERCENTAGE: "PERCENTAGE",
  UL_POINTS: "UL_POINTS",
  UL_PERCENTAGE: "UL_PERCENTAGE",
} as const;
// Wait-and-Trade type — pre-Phase-6 was inlined in LegMatrix. Homed here so
// UL W&T types (explicit direction — spot can move either way from reference)
// share the constants-rule single-source-of-truth. Mirrors backend `WatType`.
export const WAT_TYPE = {
  POINTS: "POINTS",
  PERCENTAGE: "PERCENTAGE",
  UL_POINTS_UP: "UL_POINTS_UP",
  UL_POINTS_DOWN: "UL_POINTS_DOWN",
  UL_PCT_UP: "UL_PCT_UP",
  UL_PCT_DOWN: "UL_PCT_DOWN",
} as const;
export const RANGE_SIDE = { HIGH: "HIGH", LOW: "LOW" } as const;

// UL type-group predicates for value-range validators + view-layer overlay.
// Mirrors `is_ul_*_config` helpers in backend constants.py.
export const UL_RISK_TYPES: readonly string[] = [
  RISK_MANAGEMENT_TYPE.UL_POINTS,
  RISK_MANAGEMENT_TYPE.UL_PERCENTAGE,
];
export const UL_TARGET_TYPES: readonly string[] = [
  TARGET_PROFIT_TYPE.UL_POINTS,
  TARGET_PROFIT_TYPE.UL_PERCENTAGE,
];
export const UL_TSL_TYPES: readonly string[] = [
  TSL_TYPE.UL_POINTS,
  TSL_TYPE.UL_PERCENTAGE,
];
export const UL_WAT_TYPES: readonly string[] = [
  WAT_TYPE.UL_POINTS_UP,
  WAT_TYPE.UL_POINTS_DOWN,
  WAT_TYPE.UL_PCT_UP,
  WAT_TYPE.UL_PCT_DOWN,
];

// Per-strategy leg caps. Main (primary BUY/SELL) legs and protective hedge
// (BUY-only, associate-hedge) legs have independent budgets — a hedge is an
// additive protective leg, not one of the six primary legs. Mirrors the backend
// MAX_MAIN_LEGS / MAX_HEDGE_LEGS in libs/shared/shared/constants.py.
export const MAX_MAIN_LEGS = 6;
export const MAX_HEDGE_LEGS = 6;

// Underlying indices
export const UNDERLYING = {
  NIFTY: "NIFTY",
  BANKNIFTY: "BANKNIFTY",
  FINNIFTY: "FINNIFTY",
  MIDCPNIFTY: "MIDCPNIFTY",
  SENSEX: "SENSEX",
  BANKEX: "BANKEX",
} as const;

// Underlyings that only support monthly expiry (no weekly contracts)
export const MONTHLY_ONLY_UNDERLYINGS: string[] = [UNDERLYING.BANKNIFTY, UNDERLYING.FINNIFTY, UNDERLYING.MIDCPNIFTY, UNDERLYING.BANKEX];

// Status values
export const STATUS = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  INACTIVE: "INACTIVE",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const;
export const POSITION_STATUS = {
  ENTRY_PENDING: "ENTRY_PENDING",
  OPEN: "OPEN",
  EXIT_PENDING: "EXIT_PENDING",
  CLOSED: "CLOSED",
  // Platform-only "stop watching" marker (mirrors backend PositionStatus).
  // Written by Terminate — the platform stops managing the row without any
  // broker call. Terminal + one-way; rendered with the neutral slate chip.
  TERMINATED: "TERMINATED",
} as const;
export const ENTRY_STATUS = {
  TRIGGER_PENDING: "TRIGGER_PENDING",
  OPEN: "OPEN",
  COMPLETE: "COMPLETE",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  // In-flight claim — written by backend entry placement services BEFORE the
  // broker place_order call (Phase 2 claim-then-place). Backfilled to
  // TRIGGER_PENDING / COMPLETE / REJECTED within the same Lambda invocation
  // (~1-3 sec). Surfaces to the frontend only when the schedule view is
  // polled during that window. Treat as "pending" for bucket totals — the
  // entry still needs to land at the broker. Mirror of backend
  // EntryStatus.PENDING_PLACE.
  PENDING_PLACE: "PENDING_PLACE",
  // UL Wait-and-Trade invisible-wait — no broker order rests; the row sits
  // in DDB waiting for the underlying monitor to observe spot reaching the
  // entry-at level. Semantically "armed, awaiting condition" — treat as
  // pending for bucket totals (same as TRIGGER_PENDING / PENDING_PLACE).
  // Mirror of backend EntryStatus.UL_MONITOR_PENDING (Phase 5a).
  UL_MONITOR_PENDING: "UL_MONITOR_PENDING",
} as const;
export const EXIT_STATUS = {
  OPEN: "OPEN",
  COMPLETE: "COMPLETE",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
} as const;
export const SL_STATUS = {
  TRIGGER_PENDING: "TRIGGER_PENDING",
  OPEN: "OPEN",
  COMPLETE: "COMPLETE",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  // In-flight claim — written by backend sl_placement_service BEFORE the
  // broker place_stop_loss_order call (claim-then-place ordering). Backfilled
  // to TRIGGER_PENDING within the same Lambda invocation (~1-3 sec). Surfaces
  // to the frontend only when the schedule view is polled during that window.
  // Treat as "pending" for bucket totals — the SL still needs to land at
  // the broker. Mirror of backend SlStatus.PENDING_PLACE.
  PENDING_PLACE: "PENDING_PLACE",
} as const;
export const EXIT_TYPE = {
  SL: "SL",
  TP: "TP",                     // leg-level Target Profit hit
  EXIT: "EXIT",
  EOD: "EOD",
  EXIT_FORCED: "EXIT_FORCED",
  EXPIRY_SETTLED: "EXPIRY_SETTLED",
  STRATEGY_TP: "STRATEGY_TP",   // strategy-level MTM target hit
  STRATEGY_SL: "STRATEGY_SL",   // strategy-level MTM stop hit
  HEDGE_RECYCLE: "HEDGE_RECYCLE", // associate-hedge recycle: hedge squared when its main short went flat
  UL_SL: "UL_SL",               // index spot crossed the underlying-based SL level
  UL_TP: "UL_TP",               // index spot reached the underlying-based target level
} as const;

// Basket
export const BASKET_CATEGORY = {
  CONSERVATIVE: "CONSERVATIVE",
  AGGRESSIVE: "AGGRESSIVE",
  HEDGED: "HEDGED",
  INCOME: "INCOME",
  MOMENTUM: "MOMENTUM",
} as const;
export const RISK_LEVEL = { LOW: "LOW", MEDIUM: "MEDIUM", HIGH: "HIGH" } as const;

// Marketplace publisher group slugs that always exist after deploy.
// Mirrors backend PublisherGroupSeed in libs/shared/shared/constants.py.
export const SEEDED_PUBLISHER_GROUPS = ["public", "admin_only"] as const;

// Basket status (subset — baskets only use ACTIVE/PAUSED/INACTIVE, not COMPLETED/CANCELLED)
export const BASKET_STATUS = { ACTIVE: "ACTIVE", PAUSED: "PAUSED", INACTIVE: "INACTIVE" } as const;

// Strategy status (subset — strategies only use ACTIVE/PAUSED/COMPLETED, not INACTIVE/CANCELLED)
export const STRATEGY_STATUS = { ACTIVE: "ACTIVE", PAUSED: "PAUSED", COMPLETED: "COMPLETED" } as const;

// Allocation status (subset — allocations only use ACTIVE/INACTIVE)
export const ALLOCATION_STATUS = { ACTIVE: "ACTIVE", INACTIVE: "INACTIVE" } as const;

// Mirror of backend `shared.constants.AllocationSource`. Set on BASKET_ALLOCATION
// rows when the allocation is a marketplace subscription. Self-owned allocations
// don't carry this field (or carry an empty string); readers treat any non-
// MARKETPLACE value as self-owned.
export const ALLOCATION_SOURCE = { MARKETPLACE: "MARKETPLACE" } as const;

// Broker
export const BROKER_NAME = { ZERODHA: "zerodha", ANGEL: "angel", FINVASIA: "finvasia", ZEBU: "zebu" } as const;
export const ACCOUNT_STATUS = { ENABLED: "enabled", DISABLED: "disabled", PENDING: "pending" } as const;

// Orders
export const ORDER_TAG = { ENTRY: "E", EXIT: "S" } as const;
export const ORDER_STATUS = {
  SUCCESS: "SUCCESS",
  OPEN: "OPEN",
  FAILED: "FAILED",
  PARTIAL: "PARTIAL",
  CANCELLED: "CANCELLED",
} as const;

// Days
export const DAY = { MON: "MON", TUE: "TUE", WED: "WED", THU: "THU", FRI: "FRI" } as const;
export const WEEKDAYS = Object.values(DAY) as string[];

// Short 2-letter labels for compact chip displays (MO/TU/WE/TH/FR). The
// underlying data model still uses MON-FRI — these are display-only.
export const DAY_SHORT_LABELS: Record<string, string> = {
  MON: "MO",
  TUE: "TU",
  WED: "WE",
  THU: "TH",
  FRI: "FR",
};

// Entry selection mode — INTRADAY strategies pick their firing rule via this field.
// WEEKDAY (default, legacy): fire on weekdays in entryDays.
// DTE: fire when remaining-days-to-expiry is in entryDtes. v1 supports the
// current-weekly chain only (expiry_type === "weekly", underlying not in
// MONTHLY_ONLY_UNDERLYINGS). Mirrors EntrySelectionMode in
// libs/shared/shared/constants.py.
export const ENTRY_SELECTION_MODE = { WEEKDAY: "WEEKDAY", DTE: "DTE" } as const;
export type EntrySelectionMode = typeof ENTRY_SELECTION_MODE[keyof typeof ENTRY_SELECTION_MODE];

// Allowed integer DTE values for INTRADAY DTE-mode strategies (v1).
// Covers a normal weekly expiry cycle (5 trading days: Fri → Thu).
export const INTRADAY_DTE_OPTIONS: number[] = [0, 1, 2, 3, 4];

// Editor chip labels — intent-based, used in the strategy editor's chip picker.
export const INTRADAY_DTE_EDIT_LABELS: Record<number, string> = {
  0: "Expiry day",
  1: "1 day before",
  2: "2 days before",
  3: "3 days before",
  4: "4 days before",
};

// Compact labels for at-a-glance scanning — used in editor chips and every
// read-only display (StrategyCard, StrategyTable, BasketsPage list, marketplace
// badge, schedule preview). Format: D{N} per the day-pill / DTE-badge
// convention in frontend CLAUDE.md (kept short to fit the compact chip row).
export const INTRADAY_DTE_COMPACT_LABELS: Record<number, string> = {
  0: "D0",
  1: "D1",
  2: "D2",
  3: "D3",
  4: "D4",
};

// Analytics — mirrors libs/shared/shared/constants.py DteBucket / AnalyticsDimension / AnalyticsTimeDimension
export const DTE_BUCKETS = { ZERO_DTE: "0DTE", ONE_TO_THREE: "1-3DTE", OVER_THREE: ">3DTE" } as const;
export const DTE_BUCKET_ORDER = [DTE_BUCKETS.ZERO_DTE, DTE_BUCKETS.ONE_TO_THREE, DTE_BUCKETS.OVER_THREE] as const;

export const ANALYTICS_DIMENSION = {
  STRATEGY: "strategy",
  BASKET: "basket",
  UNDERLYING: "underlying",
  EXIT_REASON: "exit_reason",
} as const;

export const ANALYTICS_TIME_DIMENSION = {
  DOW: "dow",
  HOUR: "hour",
  DTE: "dte",
} as const;

// Hours when Indian markets are open (09:15-15:40 IST) — entry-hour bucket keys.
export const ANALYTICS_TRADING_HOURS = [9, 10, 11, 12, 13, 14, 15] as const;

/**
 * Market-hours single source of truth (IST). Two backend sources back this:
 *   - OPEN/CLOSE (exchange session, 09:15–15:40 inclusive) mirrors
 *     `libs/expiry_lib/expiry_lib/trading_window.py` (NFO/BFO window).
 *   - SESSION_START/SESSION_LAST (platform managed entry/exit window,
 *     09:16–15:39) mirrors `MarketHours` in `libs/shared/shared/constants.py`
 *     (START inclusive .. END, the last selectable minute).
 * Keep both in lockstep with their respective backend source on any
 * market-timing change.
 */
export const MARKET_HOURS = {
  // Exchange session — drives the "Market Open/Closed" status indicator.
  OPEN_HOUR: 9,
  OPEN_MINUTE: 15,
  CLOSE_HOUR: 15,
  CLOSE_MINUTE: 40,
  // Platform managed window — earliest & last selectable entry/exit minute.
  SESSION_START_HOUR: 9,
  SESSION_START_MINUTE: 16,
  SESSION_LAST_HOUR: 15,
  SESSION_LAST_MINUTE: 39,
} as const;

/**
 * Position retention in days. Mirrors POSITION_RETENTION_DAYS in
 * libs/shared/shared/constants.py. The positions table TTL is set to
 * `now + POSITION_RETENTION_DAYS` on every close / cancel / EOD-finalise,
 * so trade-level drill-down on Analytics is bounded by this window.
 * Aggregate KPIs in analytics-daily have no TTL and survive forever.
 */
export const POSITION_RETENTION_DAYS = 90;

/**
 * Per-broker-account start time for the per-minute checks (SL/WAT/ORB/RE/TP/
 * TSL/MTM/broker-sync). Mirrors `MarketHours.START` + `CheckStartGate.MAX` in
 * libs/shared/shared/constants.py and the `_validate_check_start` bounds in
 * lambda_functions/broker/broker_account_manager.py. Range is [09:16, 12:00]
 * inclusive; an account with no configured value defaults to 09:16.
 */
export const CHECK_START_GATE = {
  MIN_HOUR: 9,
  MIN_MINUTE: 16,
  MAX_HOUR: 12,
  MAX_MINUTE: 0,
  DEFAULT_HOUR: 9,
  DEFAULT_MINUTE: 16,
} as const;

/**
 * Telegram push-alert categories — mirrors `TelegramNotifyCategory` in
 * libs/shared/shared/constants.py. Used by the per-category mute toggles on
 * the Account page's Telegram card. Categories are ON by default once linked;
 * muting one stores `false` for it in the user's telegram_prefs.
 */
export const TELEGRAM_NOTIFY_CATEGORY = {
  LOGIN_REQUIRED: "LOGIN_REQUIRED",
  ORDER_REJECTED: "ORDER_REJECTED",
  EXIT_FAILED: "EXIT_FAILED",
  ENTRY_FAILED: "ENTRY_FAILED",
  NEEDS_ATTENTION: "NEEDS_ATTENTION",
} as const;
export type TelegramNotifyCategory =
  typeof TELEGRAM_NOTIFY_CATEGORY[keyof typeof TELEGRAM_NOTIFY_CATEGORY];
